//Dependencies............................................
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Card, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, EditOutlined, SendOutlined } from "@ant-design/icons";
import { SubmitHandler, useForm } from "react-hook-form";

//Components..............................................
import { BASE_URL } from "../../../utils/constants";
import { FirebaseAppVariant, FirmwareBase,LatestFirmwareGlobal, MobileNotification, MobileNotificationStatusType, MobileNotificationType} from "../../../utils/interfaces";

import { NotificationInstance } from "antd/es/notification/interface";
import EditGlobalLatestFirmwareModal from "../../../Components/FirmwareManagerPageComp/Modals/EditGlobalLatestFirmwareModal";
import MobileNotificationSendModal from "../../GlobalComponents/Modals/SendMobileNotificationModal";
import { useHasAction } from "../../../hooks/useHasAction";

const { Text } = Typography;

interface Props {
  api: NotificationInstance
}

const GlobalLatestFirmwareView: React.FC<Props> = ({ api }) => {

  const canSendNotificationLatestFirmware: boolean = useHasAction("firmware_manager_send_mobile_notification");
  const canEditLatestFirmware: boolean = useHasAction("firmware_manager_edit_latest_firmware");

  const [loading, setLoading] = useState<boolean>(false);
  const [latestglobalFirmwareData, setlatestglobalFirmwareData] = useState<FirmwareBase | null>(null);
  const [FirmwareData, setFirmwareData] = useState<FirmwareBase[]>([]);

  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showSendNotificationModal, setShowSendNotificationModal] = useState<boolean>(false);
  const [sendingMobileNotification, setSendingMobileNotification] = useState<boolean>(false);

  useEffect(() => {
    handleGetLatestFirmwareData();
    handleGetFirmwareData();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const toggleEditGlobalLatestFirmwareModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowEditModal(true);
    } else {
      setShowEditModal(false);
    }
  }

  const toggleSendMobileNotificationModal = (makeVisible: boolean) => {
    if(makeVisible){
      setShowSendNotificationModal(true);
    } else {
      setShowSendNotificationModal(false);
    }
  }

  const {
    control: globalLatestFirmwareEditControl,
    handleSubmit: globalLatestFirmwareEditSubmit,
    formState: { isValid: globalLatestFirmwareEditIsValid, isDirty: globalLatestFirmwareEditIsDirty },
    // reset: globalLatestFirmwareEditReset,
  } = useForm<LatestFirmwareGlobal>({mode: "onChange"});

  const {
    control: sendMobileNotificationControl,
    handleSubmit: sendMobileNotificationHandleSubmit,
    formState: { errors: sendMobileNotificationErrors, isValid: sendMobileNotificationIsValid },
    reset: sendMobileNotificationReset
  } = useForm<MobileNotification>({mode: "onChange"});

  const handleSendNotification = (firmware: FirmwareBase) => {
    const defaultTitle = `Firmware version ${firmware.version_name} available`;
    const defaultBody = `New firmware version is available for your CANNY Device`;

    sendMobileNotificationReset({title: defaultTitle, body: defaultBody})
    toggleSendMobileNotificationModal(true);
  };

  const handleGetLatestFirmwareData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}get_latest_firmware`);
      setlatestglobalFirmwareData(response.data.content);
    } catch (err) {
      api.error({
        title: 'Fetching failed',
        description: `Failed to fetch latest firmware base data.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGetFirmwareData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}get_firmware_bases`);
      setFirmwareData(response.data.content);
    } catch (err) {
      api.error({
        title: 'Fetching failed',
        description: `Failed to fetch firmware base data.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeLatestFirmware: SubmitHandler<LatestFirmwareGlobal> = async (data) => {
    const firmwareId = data.firmware_base_id;
    const firmware = FirmwareData?.find((f) => f.id === firmwareId);
    setLoading(true);
    try {
      if (firmware) {
        await axios.post(`${BASE_URL}/set_latest_firmware_global/${firmware.id}`)
        handleGetLatestFirmwareData();
        api.success({
          title: "Latest firmware set successfully",
          description: `Latest firmware set to ${firmware.version_name}`,
          placement: "bottomRight",
          showProgress: true
        });
      }
    } catch (error: any) {
      api.error({
        title: "Error setting latest firmware",
        description: `Failed to set latest firmware: ${error.message}`,
        placement: "bottomRight",
        showProgress: true
      });
    } finally {
      toggleEditGlobalLatestFirmwareModal(false);
      setLoading(false);
    }
  };

  const handleCreateSendMobileNotification = async (data: MobileNotification, appVariant: FirebaseAppVariant) => {
    if(latestglobalFirmwareData == null) return
    setSendingMobileNotification(true);
    try {
      await axios.post(`${BASE_URL}send_firmware_update_notification`,{
        title: data.title,
        body: data.body,
        appVariant: appVariant,
        firmware_base_id: latestglobalFirmwareData.id
      });
      api.success({
        title: 'Sending Successful',
        description: `Mobile Notification was sent successfully.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } catch (err) {
      api.error({
        title: 'Sending Failed',
        description: `Failed to send Mobile notification.`,
        placement: 'bottomRight',
        showProgress: true
      });
    } finally {
      setSendingMobileNotification(false);
      toggleSendMobileNotificationModal(false);
      handleGetLatestFirmwareData();
    }
  };

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{textAlign: 'left' }}>Latest Firmware Management</span>
          <span style={{textAlign: 'right', display: 'flex', gap: '5px' }}>
            {latestglobalFirmwareData && (
              <Tooltip title={
                latestglobalFirmwareData.notification_status === MobileNotificationStatusType.SENT
                  ? "Notification already sent"
                  : latestglobalFirmwareData.notification_status === MobileNotificationStatusType.PENDING
                  ? "Notification pending"
                  : "Send notification"
              }>
                <Button
                  icon={
                    latestglobalFirmwareData.notification_status === MobileNotificationStatusType.SENT
                      ? <CheckCircleOutlined />
                      : latestglobalFirmwareData.notification_status === MobileNotificationStatusType.PENDING
                      ? <ClockCircleOutlined />
                      : <SendOutlined />
                  }
                  style={{ marginRight: "16px" }}
                  disabled={latestglobalFirmwareData.notification_status === MobileNotificationStatusType.SENT || !canSendNotificationLatestFirmware}
                  onClick={() => handleSendNotification(latestglobalFirmwareData)}
                >
                  Send
                </Button>
              </Tooltip>
            )}
            <Button
              icon={<EditOutlined />}
              onClick={async () => {
                await handleGetFirmwareData();
                toggleEditGlobalLatestFirmwareModal(true);
              }}
              style={{fontSize:12}}
              type="primary"
              loading={loading}
              disabled={!canEditLatestFirmware}
            >
              Edit Latest Firmware</Button>
          </span>
        </div>
      }
    >
      {loading ? <Spin/> :
        <Space>
          <Text>Global latest version is currently set to:</Text>
          {latestglobalFirmwareData
            ? <Tag color="blue" style={{ fontSize: 14, padding: "4px 10px" }}>{latestglobalFirmwareData.version_name}</Tag>
            : <Tag>Not set</Tag>}
        </Space>
      }
      <EditGlobalLatestFirmwareModal
        showEditModal={showEditModal}
        toggleEditModal={toggleEditGlobalLatestFirmwareModal}
        loadingButtons={loading}
        firmwareVersions={FirmwareData.filter(fw => fw.status === 'RELEASED') || []}
        updateCanny={handleChangeLatestFirmware}
        cannyEditSubmit={globalLatestFirmwareEditSubmit}
        control={globalLatestFirmwareEditControl}
        isValid={globalLatestFirmwareEditIsValid}
        isDirty={globalLatestFirmwareEditIsDirty}
      />
      <MobileNotificationSendModal
        showSendMobileNotificationModal={showSendNotificationModal}
        toggleSendMobileNotificationModal={toggleSendMobileNotificationModal}
        handleSubmit={sendMobileNotificationHandleSubmit}
        errors={sendMobileNotificationErrors}
        onSubmit={handleCreateSendMobileNotification}
        control={sendMobileNotificationControl}
        isValid={sendMobileNotificationIsValid}
        isSubmitting={sendingMobileNotification}
        notificationTopic={MobileNotificationType.FIRMWARE_UPDATE}
      />
    </Card>
  );
};

export default GlobalLatestFirmwareView;
